import React, { useEffect, useMemo } from "react";
import { useStore } from "../../store/store";
import { useRequiredMsgs } from "../../store/requiredMsgs";
import fractionToInt from "../../util/fractionToInt";
import { useTheme } from "@mui/material/styles";
import { addCentorScreens } from "../../initialValues/screens";
import { screenType as screenTypeOptions } from "../ScreenType/defaults";
import { finAlumBifold } from "../Fin/defaults";

function useScreenSelection(width: number, widthRight: number) {
  const theme = useTheme();
  const addToQuote = useStore((state) => state.addToQuote);

  const material = useStore((state) => state.quote.material);

  const doorType = useStore((state) => state.quote.doorType);
  const screen = useStore((state) => state.quote.addScreen);
  const screenVal = screen && screen.replace(" (upcharge applies)", ""); //backwards compatability

  const screenLayout = useStore((state) => state.quote.screenLayout);
  const screenColorChoice = useStore((state) => state.quote.screenColor);
  const shadeMeshColor = useStore((state) => state.quote.shadeMeshColor);
  const cornerType = useStore((state) => state.quote.cornerType);
  const sillStyle = useStore((state) => state.quote.sillStyle);

  const height = useStore((state) => state.quote.height);
  const heightFraction = useStore((state) => state.quote.heightFraction);

  const heightInt = fractionToInt(height, heightFraction);

  const swingType = useStore((state) => state.quote.swingType);

  const exactConfig = useStore((state) => state.quote.exactConfig);

  const handleErrMsgChange = useStore((state) => state.handleError);

  const isBifoldAlum =
    material === "All Aluminum" &&
    (doorType === "Bi-fold" || doorType === "Bi-fold Window");

  const isBifoldCombo = doorType === "Bi-fold Door/Window Combo"

  const isScreenNotRequired = isBifoldAlum || isBifoldCombo

  const centorWidthSub = 1;

  const centorCushion = 0.125; //added on 05/22/2025 per Brian because screen sizes were "too exact"

  const centorWidth = width - centorWidthSub - centorCushion;
  const centorHeight = sillStyle?.includes("Ultra")
    ? heightInt + 1.1875 - centorCushion
    : heightInt - 0.5625 - centorCushion;

  const widerThan16 = centorWidth > 181;

  const oneDirectionConfig =
    doorType === "Swing" &&
    swingType === "Single Swing" &&
    exactConfig.slice(-2) === "LH"
      ? "sL"
      : doorType === "Swing" &&
          swingType === "Single Swing" &&
          exactConfig.slice(-2) === "RH"
        ? "sR"
        : doorType.includes("Bi-fold") && exactConfig.length === 2
          ? exactConfig
          : "";

  const findRequired = useRequiredMsgs((state) => state.findRequired);
  const removeRequired = useRequiredMsgs((state) => state.removeRequired);

  const isCornerUnit = doorType?.includes("Corner");

  useEffect(() => {
    if (screenVal !== "Add Shade" && screenVal !== "Add Screen") return;

    if (oneDirectionConfig && oneDirectionConfig[1]) {
      defaultLayout();
    }
  }, [screenVal, exactConfig]);

  const defaultLayout = () => {
    if (!oneDirectionConfig || !oneDirectionConfig[1]) return;

    if (oneDirectionConfig[1] === "L") {
      screenVal === "Add Shade"
        ? addToQuote({ screenLayout: "Left Shade" })
        : addToQuote({ screenLayout: "Single Compress Left" });
    }
    if (oneDirectionConfig[1] === "R") {
      screenVal === "Add Shade"
        ? addToQuote({ screenLayout: "Right Shade" })
        : addToQuote({ screenLayout: "Single Compress Right" });
    }
  };

  const handleAddScreenClick = (label: string) => {
    // console.log("Label: ", label);
    // console.log("Screen Val", screenVal);
    // console.log("Screen Layout", screenLayout);

    const checked = label === screenVal;

    handleErrMsgChange("jambErr", "");

    if (checked) {
      addToQuote(
        {
          addScreen: "",
          jamb: "",
        },
        "addScreen",
      );
    } else {
      if (findRequired("Add Screen")) {
        removeRequired("Add Screen");
      }
      if (findRequired("Centor Screen")) {
        removeRequired("Centor Screen");
      }
      let update = {
        addScreen: label,
        //added for when screen type accordion isnt shown such as bifold aluminum and we need to set screenType
        screenType:
          screenTypeOptions.find((itm) => itm.label.includes("Centor"))
            ?.label || "",
        jamb: "",
        screenLayout: "",
      };

      if (isBifoldAlum) {
        update["flashingFin"] = finAlumBifold.label;
        update["flashingFinConfig"] = 'Standard 1" Setback';
      }

      addToQuote(update, "addScreen");
    }
  };

  const handleClick =
    (label: string, choice: string, key: string, reqKey: string) => () => {
      const checked = label === choice;
      if (checked) {
        addToQuote({ [key]: "" });
      } else {
        addToQuote({ [key]: label });
        if (findRequired(reqKey)) removeRequired(reqKey);
      }
    };

  var centor =
    (doorType === "Bi-fold" || doorType === "Bi-fold Window") &&
    exactConfig.includes("L") &&
    exactConfig.includes("R") &&
    centorWidth <= 165
      ? addCentorScreens
      : isCornerUnit &&
          (centorWidth > 165 ||
            widthRight - centorWidthSub - centorCushion > 165)
        ? addCentorScreens.filter((itm) => itm.label.includes("Double Screen"))
        : widerThan16 || isCornerUnit
          ? addCentorScreens.filter((itm) => itm.label.includes("Double"))
          : !widerThan16 && centorWidth > 167
            ? addCentorScreens.filter((itm) => !itm.label.includes("Combo"))
            : // : !widerThan16 && !isSwingPair
              // ? addCentorScreens.filter((itm) => !itm.label.includes("Double"))
              addCentorScreens;

  if (centorHeight >= 114) {
    centor = centor.filter(
      (itm) => !itm.label.includes("Combo") && !itm.label.includes("Shade"),
    );
  }

  const displayedImage = useMemo(() => {
    if (!screenVal) return "";

    if (isCornerUnit) {
      let img =
        cornerType === "Inside"
          ? "Double Screen Inside Corner"
          : "Double Screen Outside Corner";

      if (screenVal === "Add Double Shade") {
        img = `${img} Shade`;
      }
      return img;
    }

    if (screenVal.includes("Combo")) {
      if (screenLayout === "Left Shade/Right Screen") {
        return "Combo Screen Left";
      }

      if (screenLayout === "Right Shade/Left Screen") {
        return "Combo Screen Right";
      }

      if (screenLayout === "Left Screen/Right Shade") {
        return "Combo Screen Right";
      }

      return "";
    }

    if (screenVal === "Add Double Shade") {
      return "Double Shade";
    }

    if (screenVal === "Add Double Screen") {
      return "Double Screen";
    }

    if (screenVal === "Add Shade") {
      if (screenLayout === "Left Shade") {
        return "Shade Left";
      }

      if (screenLayout === "Right Shade") {
        return "Shade Right";
      }
      return "";
    }

    if (oneDirectionConfig && oneDirectionConfig[1]) {
      if (oneDirectionConfig[1] === "L") {
        return "Single Screen Compress Left";
      }
      if (oneDirectionConfig[1] === "R") {
        return "Single Screen Compress Right";
      }
    }

    if (screenLayout?.includes("Left")) {
      return "Single Screen Compress Left";
    }

    if (screenLayout?.includes("Right")) {
      return "Single Screen Compress Right";
    }
  }, [screenVal, oneDirectionConfig, doorType, cornerType, screenLayout]);

  const hasShade =
    screenVal === "Add Shade" ||
    screenVal === "Add Double Shade" ||
    screenVal === "Add Combo Screen/Shade"
      ? true
      : false;

  return {
    hasShade,
    displayedImage,
    handleClick,
    handleAddScreenClick,
    centor,
    centorHeight,
    screenColorChoice,
    screenLayout,
    shadeMeshColor,
    screenVal,
    screen,
    isCornerUnit,
    oneDirectionConfig,
    widerThan16,
    theme,
    centorWidthSub,
    centorWidth,
    centorCushion,
    isScreenNotRequired
  };
}

export default useScreenSelection;
